import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RouterIcon from '@mui/icons-material/Router';
import SearchIcon from '@mui/icons-material/Search';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TuneIcon from '@mui/icons-material/Tune';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import MigracionOltHistorial from './MigracionOltHistorial';
import ServiceProfilesDialog from './ServiceProfilesDialog';
import MigracionOltCorridaPanel from './MigracionOltCorridaPanel';
import {
  BitacoraCliente,
  BloqueJson,
  ESTADO_FINAL_CFG,
  seg,
} from './MigracionOltBitacora';
import {
  useMigracionOltConsultaAccion,
  useMigracionOltConsultaActiva,
  useMigracionOltConsultaContexto,
  useMigracionOltConsultaEstado,
  useMigracionOltConsultaIniciar,
  useMigracionOltCorridaAccion,
  useMigracionOltCorridaActiva,
  useMigracionOltCorridaEstado,
  useMigracionOltCorridaIniciar,
  useMigracionOltEjecuciones,
  useMigracionOltCargarExcel,
  useMigracionOltConfiguracionOnt,
  useMigracionOltConsultarOnt,
  useMigracionOltMigrarCliente,
  useMigracionOltReintentarActivacion,
  useMigracionOltPrepararAprov,
  useMigracionOltServiceProfile,
  type IMigracionOltConfigEntrada,
  type IMigracionOltEstadoSerie,
  type IMigracionOltEstadoFinal,
  type IMigracionOltEstadoPrep,
  type IMigracionOltEstadoServiceProfile,
  type IMigracionOltExcel,
  type IMigracionOltPaso1,
  type IMigracionOltPaso2,
  type IMigracionOltPaso3,
  type IMigracionOltPaso4,
  type IMigracionOltPaso5Prep,
  type IMigracionOltResultadoMigracion,
  type IMigracionOltResultadoSerie,
} from '@/shared/actions/erp/tickets-soporte-cliente/softnet-import.actions';

/** IPv4 simple: el microservicio valida en serio, esto solo evita el viaje. */
const ES_IPV4 = /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d?\d)$/;

/**
 * Devuelve el objeto sólo si trae esas claves como arreglos; si no, null.
 *
 * Se usa con lo que llega del contexto que guarda el servidor: esos objetos
 * pasaron por jsonb y pudo escribirlos una versión anterior de la pantalla.
 * Los componentes leen `.length` y `.map` sin preguntar, así que uno recortado
 * tumba el render completo y deja la página en blanco.
 */
const conArreglos = <T,>(obj: unknown, ...claves: string[]): T | null => {
  if (!obj || typeof obj !== 'object') return null;
  const o = obj as Record<string, unknown>;
  return claves.every((c) => Array.isArray(o[c])) ? (obj as T) : null;
};

/**
 * Medido contra Softnet el 2026-10-05: 71 series tardaron 44 s, o sea ~0,62 s
 * por serie. Sirve para estimar lo que falta del paso 2.
 *
 * El paso 2 ya NO se parte en lotes acá: lo ejecuta el micro con su propia
 * cola (ver useMigracionOltConsultaIniciar). El lote del navegador existía
 * para no pasarse del timeout de nginx y para que la barra se moviera; sin
 * peticiones largas de por medio, ninguna de las dos razones aplica.
 */
const SEGUNDOS_POR_SERIE = 0.7;

const ESTADO_CFG: Record<
  IMigracionOltEstadoSerie,
  { label: string; color: 'success' | 'warning' | 'default' | 'error'; ayuda: string }
> = {
  ENCONTRADO: {
    label: 'Con contrato',
    color: 'success',
    ayuda: 'Softnet devolvió al menos un contrato para esta serie.',
  },
  NO_EXISTE: {
    label: 'Sin registro',
    color: 'warning',
    ayuda: 'Softnet respondió que no existe información con esa serie.',
  },
  SIN_DATOS: {
    label: 'Sin datos',
    color: 'default',
    ayuda: 'Softnet respondió correctamente pero sin contratos.',
  },
  ERROR: {
    label: 'Error',
    color: 'error',
    ayuda: 'No se pudo consultar (Softnet caído o error de red).',
  },
};

const ESTADO_SP_CFG: Record<
  IMigracionOltEstadoServiceProfile,
  { label: string; color: 'success' | 'warning' | 'default' | 'error'; ayuda: string }
> = {
  CON_SERVICE_PROFILE: {
    label: 'Con service profile',
    color: 'success',
    ayuda: 'El producto del inventario tiene service profile definido.',
  },
  SIN_SERVICE_PROFILE: {
    label: 'Sin service profile',
    color: 'warning',
    ayuda:
      'La serie resolvió un producto, pero ese producto no tiene service profile en Configuración de red.',
  },
  SIN_PRODUCTO: {
    label: 'Sin producto',
    color: 'default',
    ayuda: 'La serie no está en el inventario del ERP, así que no hay producto del que sacar el perfil.',
  },
  AMBIGUO: {
    label: 'Ambiguo',
    color: 'error',
    ayuda:
      'La serie está cargada más de una vez en el inventario apuntando a productos con service profiles distintos. No se elige uno: requiere revisión.',
  },
};


/**
 * Saca el nombre y el idCliente del cliente desde la respuesta cruda de Softnet
 * del paso 2 (`data[0].contrato`).
 *
 * El micro de provisionamiento exige `identificacion` NO vacía (devuelve 400 "La
 * identificación es requerida"), y Softnet no entrega cédula ni RUC en esta
 * respuesta: lo único identificador disponible es `idCliente`, así que es lo
 * que se usa.
 */
const datosClienteDeSoftnet = (
  response: unknown,
): { nombre: string; identificacion: string } => {
  const contrato = (response as any)?.data?.[0]?.contrato;
  return {
    nombre: String(contrato?.nombre_cliente ?? '').trim(),
    identificacion: String(contrato?.idCliente ?? '').trim(),
  };
};

/** De dónde salió el perfil de cada serie. */
const ORIGEN_CFG: Record<
  'ERP' | 'EXCEL' | 'AMBOS' | 'NINGUNO',
  { label: string; color: 'success' | 'info' | 'default'; ayuda: string }
> = {
  AMBOS: {
    label: 'ERP + Excel',
    color: 'success',
    ayuda: 'El inventario del ERP y el Excel coinciden en el service profile.',
  },
  ERP: {
    label: 'ERP',
    color: 'info',
    ayuda: 'Solo el inventario del ERP tiene el perfil; esta serie no está en el Excel.',
  },
  EXCEL: {
    label: 'Excel',
    color: 'info',
    ayuda:
      'Lo aportó el Excel de la OLT: el inventario del ERP no tenía perfil para esta serie.',
  },
  NINGUNO: {
    label: 'Sin fuente',
    color: 'default',
    ayuda: 'Ni el inventario del ERP ni el Excel tienen service profile para esta serie.',
  },
};


interface MetricaProps {
  label: string;
  value: React.ReactNode;
  color?: string;
}

const Metrica: React.FC<MetricaProps> = ({ label, value, color = 'text.primary' }) => (
  <Paper variant="outlined" sx={{ px: 2, py: 1.5, minWidth: 130 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={600} color={color}>
      {value}
    </Typography>
  </Paper>
);

/** Paso 1: seriales leídos de la OLT. */
const ResultadoPaso1: React.FC<{ data: IMigracionOltPaso1 }> = ({ data }) => {
  const copiarSeries = () => {
    navigator.clipboard?.writeText(data.series_unicas.join('\n'));
  };

  return (
    <Accordion defaultExpanded variant="outlined" sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <RouterIcon color="primary" />
          <Typography fontWeight={600}>Paso 1 · ONT configuradas en la OLT</Typography>
          <Chip size="small" label={data.ip_olt} variant="outlined" />
          <Chip
            size="small"
            color={data.series_unicas.length > 0 ? 'success' : 'warning'}
            label={`${data.series_unicas.length} serie(s)`}
          />
          <Chip size="small" variant="outlined" label={seg(data.duracion_ms)} />
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Metrica label="Total ONT (OLT)" value={data.total_ONTs ?? '—'} />
          <Metrica label="Series únicas" value={data.series_unicas.length} color="success.main" />
          <Metrica
            label="Duplicados"
            value={data.duplicados}
            color={data.duplicados > 0 ? 'warning.main' : 'text.primary'}
          />
          <Metrica label="Ejecutado por" value={data.us_creacion} />
        </Stack>

        {data.mensaje && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            {data.mensaje}
          </Alert>
        )}

        {data.duplicados > 0 && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            La OLT devolvió <strong>{data.duplicados}</strong> serial(es) repetido(s). Se
            consulta Softnet solo con la lista sin duplicados.
          </Alert>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Seriales</Typography>
          <Button size="small" startIcon={<ContentCopyIcon />} onClick={copiarSeries}>
            Copiar
          </Button>
        </Stack>

        {data.series_unicas.length === 0 ? (
          <Alert severity="warning" variant="outlined">
            La consulta no devolvió seriales. Ojo: una lista vacía no confirma por sí sola que
            la OLT esté accesible.
          </Alert>
        ) : (
          <Box
            sx={{
              maxHeight: 220,
              overflow: 'auto',
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: 13,
              lineHeight: 1.9,
            }}
          >
            {data.series_unicas.map((s) => (
              <Box key={s} component="span" sx={{ mr: 2 }}>
                {s}
              </Box>
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

/** Una fila del paso 2, con su JSON crudo desplegable. */
const FilaSerie: React.FC<{ item: IMigracionOltResultadoSerie }> = ({ item }) => {
  const [abierto, setAbierto] = useState(false);
  const cfg = ESTADO_CFG[item.estado] ?? ESTADO_CFG.SIN_DATOS;

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ fontFamily: 'monospace' }}>{item.serie}</TableCell>
        <TableCell>
          <Tooltip title={cfg.ayuda}>
            <Chip size="small" label={cfg.label} color={cfg.color} variant="outlined" />
          </Tooltip>
        </TableCell>
        <TableCell align="right">{item.num_contratos ?? 0}</TableCell>
        <TableCell sx={{ maxWidth: 320 }}>
          <Typography variant="body2" noWrap title={item.mensaje}>
            {item.mensaje || '—'}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Button size="small" onClick={() => setAbierto((v) => !v)} disabled={!item.response}>
            {abierto ? 'Ocultar' : 'Ver JSON'}
          </Button>
        </TableCell>
      </TableRow>
      {abierto && (
        <TableRow>
          <TableCell colSpan={5} sx={{ bgcolor: 'action.hover' }}>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                maxHeight: 360,
                overflow: 'auto',
                fontSize: 12,
                fontFamily: 'monospace',
              }}
            >
              {JSON.stringify(item.response, null, 2)}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

/** Paso 2: respuesta de Softnet por cada serie. */
const ResultadoPaso2: React.FC<{ data: IMigracionOltPaso2 }> = ({ data }) => {
  const [filtro, setFiltro] = useState<'todos' | IMigracionOltEstadoSerie>('todos');

  const visibles = useMemo(
    () => (filtro === 'todos' ? data.resultados : data.resultados.filter((r) => r.estado === filtro)),
    [data.resultados, filtro],
  );

  return (
    <Accordion defaultExpanded variant="outlined" sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <CloudSyncIcon color="primary" />
          <Typography fontWeight={600}>Paso 2 · Consulta a Softnet por serie</Typography>
          <Chip size="small" color="success" label={`${data.encontrados} con contrato`} />
          <Chip size="small" color="warning" label={`${data.no_existen} sin registro`} />
          {data.errores > 0 && (
            <Chip size="small" color="error" label={`${data.errores} error(es)`} />
          )}
          <Chip size="small" variant="outlined" label={seg(data.duracion_ms)} />
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Metrica label="Consultadas" value={data.consultadas} />
          <Metrica label="Con contrato" value={data.encontrados} color="success.main" />
          <Metrica label="Sin registro" value={data.no_existen} color="warning.main" />
          <Metrica label="Sin datos" value={data.sin_datos} />
          <Metrica
            label="Errores"
            value={data.errores}
            color={data.errores > 0 ? 'error.main' : 'text.primary'}
          />
        </Stack>

        {data.truncado && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            Se consultaron <strong>{data.consultadas}</strong> de{' '}
            <strong>{data.solicitadas}</strong> series: el tope por ejecución es{' '}
            {data.max_series_por_ejecucion}. El resto queda sin consultar.
          </Alert>
        )}

        <ToggleButtonGroup
          size="small"
          exclusive
          value={filtro}
          onChange={(_, v) => v && setFiltro(v)}
          sx={{ mb: 2, flexWrap: 'wrap' }}
        >
          <ToggleButton value="todos">Todos ({data.resultados.length})</ToggleButton>
          <ToggleButton value="ENCONTRADO">Con contrato ({data.encontrados})</ToggleButton>
          <ToggleButton value="NO_EXISTE">Sin registro ({data.no_existen})</ToggleButton>
          <ToggleButton value="SIN_DATOS">Sin datos ({data.sin_datos})</ToggleButton>
          {data.errores > 0 && <ToggleButton value="ERROR">Errores ({data.errores})</ToggleButton>}
        </ToggleButtonGroup>

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Serie ONT</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Contratos
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Mensaje de Softnet</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Respuesta
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Sin resultados para este filtro.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibles.map((item) => <FilaSerie key={item.serie} item={item} />)
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};

/**
 * Paso 3: de las series CON contrato, cuáles tienen service profile y cuáles no.
 *
 * El service profile sale del producto del inventario al que apunta la serie
 * (api_series → api_producto.service_profile), que es la misma fuente que pinta
 * la pantalla Configuración de red → Service Profile.
 */
const ResultadoPaso3: React.FC<{ data: IMigracionOltPaso3 }> = ({ data }) => {
  const [filtro, setFiltro] = useState<
    'todos' | 'DISCREPANCIA' | 'DESDE_EXCEL' | IMigracionOltEstadoServiceProfile
  >('todos');

  const visibles = useMemo(() => {
    if (filtro === 'todos') return data.resultados;
    // Dos filtros transversales al estado: los conflictos entre fuentes y lo
    // que solo el Excel pudo resolver. Son las dos vistas que de verdad se
    // revisan antes de aprovisionar.
    if (filtro === 'DISCREPANCIA') {
      return data.resultados.filter((r) => r.erp_excel_coinciden === false);
    }
    if (filtro === 'DESDE_EXCEL') {
      return data.resultados.filter((r) => r.origen === 'EXCEL');
    }
    return data.resultados.filter((r) => r.estado === filtro);
  }, [data.resultados, filtro]);

  const copiarSeries = () => {
    navigator.clipboard?.writeText(visibles.map((v) => v.serie).join('\n'));
  };

  return (
    <Accordion defaultExpanded variant="outlined" sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <SettingsEthernetIcon color="primary" />
          <Typography fontWeight={600}>Paso 3 · Service profile del producto</Typography>
          <Chip size="small" color="success" label={`${data.con_service_profile} con perfil`} />
          <Chip size="small" color="warning" label={`${data.sin_service_profile} sin perfil`} />
          {data.sin_producto > 0 && (
            <Chip size="small" label={`${data.sin_producto} sin producto`} />
          )}
          {data.ambiguos > 0 && (
            <Chip size="small" color="error" label={`${data.ambiguos} ambiguo(s)`} />
          )}
          <Chip size="small" variant="outlined" label={seg(data.duracion_ms)} />
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Metrica label="Series evaluadas" value={data.solicitadas} />
          <Metrica label="Con perfil" value={data.con_service_profile} color="success.main" />
          {data.excel_cargado && (
            <Metrica
              label="Aportadas por el Excel"
              value={data.desde_excel}
              color={data.desde_excel > 0 ? 'info.main' : 'text.primary'}
            />
          )}
          <Metrica label="Sin perfil" value={data.sin_service_profile} color="warning.main" />
          <Metrica label="Sin producto" value={data.sin_producto} />
          <Metrica
            label="Ambiguos"
            value={data.ambiguos}
            color={data.ambiguos > 0 ? 'error.main' : 'text.primary'}
          />
          {data.excel_cargado && (
            <Metrica
              label="ERP ≠ Excel"
              value={data.discrepancias}
              color={data.discrepancias > 0 ? 'error.main' : 'text.primary'}
            />
          )}
          <Metrica label="Perfiles distintos" value={data.service_profiles.length} />
        </Stack>

        {!data.excel_cargado && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
            Se validó solo contra el inventario del ERP. Cargando el Excel de perfiles de la OLT
            se resuelven además los modelos que el inventario no tiene.
          </Alert>
        )}

        {data.desde_excel > 0 && (
          <Alert severity="success" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
            El Excel resolvió <strong>{data.desde_excel}</strong> serie(s) que el inventario del
            ERP no podía: sin el archivo habrían quedado sin perfil.
          </Alert>
        )}

        {data.discrepancias > 0 && (
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            <strong>{data.discrepancias}</strong> serie(s) tienen un service profile distinto en
            el ERP y en el Excel. No se eligió ninguno: hay que decidir cuál es el correcto antes
            de aprovisionar. Filtra por «ERP ≠ Excel» para verlas.
          </Alert>
        )}

        {data.service_profiles.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              Perfiles encontrados:
            </Typography>
            {data.service_profiles.map((sp) => (
              <Chip key={sp} size="small" variant="outlined" color="success" label={sp} />
            ))}
          </Stack>
        )}

        {data.sin_service_profile > 0 && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            <strong>{data.sin_service_profile}</strong> serie(s) resolvieron un producto que{' '}
            <strong>no tiene service profile</strong> configurado. Hay que definirlo en
            Configuración de red → Service Profile antes de poder aprovisionarlas.
          </Alert>
        )}

        {data.ambiguos > 0 && (
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            <strong>{data.ambiguos}</strong> serie(s) están cargadas más de una vez en el
            inventario apuntando a productos con perfiles distintos. No se eligió ninguno: hay que
            corregir el inventario.
          </Alert>
        )}

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filtro}
            onChange={(_, v) => v && setFiltro(v)}
            sx={{ flexWrap: 'wrap' }}
          >
            <ToggleButton value="todos">Todas ({data.resultados.length})</ToggleButton>
            <ToggleButton value="CON_SERVICE_PROFILE">
              Con perfil ({data.con_service_profile})
            </ToggleButton>
            <ToggleButton value="SIN_SERVICE_PROFILE">
              Sin perfil ({data.sin_service_profile})
            </ToggleButton>
            {data.sin_producto > 0 && (
              <ToggleButton value="SIN_PRODUCTO">Sin producto ({data.sin_producto})</ToggleButton>
            )}
            {data.ambiguos > 0 && (
              <ToggleButton value="AMBIGUO">Ambiguos ({data.ambiguos})</ToggleButton>
            )}
            {data.desde_excel > 0 && (
              <ToggleButton value="DESDE_EXCEL">
                Solo del Excel ({data.desde_excel})
              </ToggleButton>
            )}
            {data.discrepancias > 0 && (
              <ToggleButton value="DISCREPANCIA">
                ERP ≠ Excel ({data.discrepancias})
              </ToggleButton>
            )}
          </ToggleButtonGroup>

          <Button size="small" startIcon={<ContentCopyIcon />} onClick={copiarSeries}>
            Copiar series del filtro
          </Button>
        </Stack>

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Serie ONT</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Service profile</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Origen</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Producto (inventario ERP)</TableCell>
                {data.excel_cargado && (
                  <TableCell sx={{ fontWeight: 600 }}>
                    Datos del Excel para aprovisionar
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={data.excel_cargado ? 6 : 5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Sin resultados para este filtro.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibles.map((item) => {
                  const cfg = ESTADO_SP_CFG[item.estado];
                  const contradice = item.erp_excel_coinciden === false;
                  return (
                    <TableRow
                      key={item.serie}
                      hover
                      sx={contradice ? { bgcolor: 'error.lighter' } : undefined}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {item.serie}
                      </TableCell>

                      <TableCell>
                        <Tooltip arrow title={cfg.ayuda}>
                          <Chip size="small" label={cfg.label} color={cfg.color} variant="outlined" />
                        </Tooltip>
                      </TableCell>

                      {/* Perfil resuelto. Si las fuentes se contradicen se muestran
                          las dos, porque elegir una por nuestra cuenta sería
                          aprovisionar a ciegas. */}
                      <TableCell>
                        {contradice ? (
                          <Stack spacing={0.5}>
                            <Chip
                              size="small"
                              color="error"
                              label={`ERP: ${item.service_profile_erp}`}
                              sx={{ fontFamily: 'monospace' }}
                            />
                            <Chip
                              size="small"
                              color="error"
                              variant="outlined"
                              label={`Excel: ${item.service_profile_excel}`}
                              sx={{ fontFamily: 'monospace' }}
                            />
                          </Stack>
                        ) : item.service_profile ? (
                          <Chip
                            size="small"
                            color="success"
                            label={item.service_profile}
                            sx={{ fontFamily: 'monospace' }}
                          />
                        ) : item.service_profiles_posibles?.length ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {item.service_profiles_posibles.map((sp) => (
                              <Chip key={sp} size="small" color="error" variant="outlined" label={sp} />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Tooltip arrow title={ORIGEN_CFG[item.origen].ayuda}>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={ORIGEN_CFG[item.origen].color}
                            label={ORIGEN_CFG[item.origen].label}
                          />
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">{item.producto ?? '—'}</Typography>
                        {item.producto_codigo && (
                          <Typography variant="caption" color="text.secondary">
                            {item.producto_codigo}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Lo que el Excel aporta y hará falta al aprovisionar. */}
                      {data.excel_cargado && (
                        <TableCell>
                          {item.en_excel ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {item.plan_excel && (
                                <Chip size="small" variant="outlined" label={item.plan_excel} />
                              )}
                              {item.line_profile_excel && (
                                <Tooltip arrow title="Line profile actual en la OLT (a reemplazar)">
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={item.line_profile_excel}
                                    sx={{ fontFamily: 'monospace' }}
                                  />
                                </Tooltip>
                              )}
                              {item.id_line_profile && (
                                <Tooltip arrow title="ID del line profile destino">
                                  <Chip
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    label={`LP ${item.id_line_profile}`}
                                  />
                                </Tooltip>
                              )}
                              {item.id_service_profile && (
                                <Tooltip arrow title="ID del service profile destino">
                                  <Chip
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    label={`SP ${item.id_service_profile}`}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              no está en el Excel
                            </Typography>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};


/**
 * Carga del Excel de perfiles de la OLT (opcional, al inicio del flujo).
 *
 * Complementa la validación del paso 3: el archivo trae el service profile de
 * modelos que el inventario del ERP no tiene, más el line profile, el plan y los
 * IDs de ambos perfiles, que son los datos que harán falta al aprovisionar.
 */
const CargaExcel: React.FC<{
  excel: IMigracionOltExcel | null;
  cargando: boolean;
  deshabilitado: boolean;
  onArchivo: (f: File) => void;
  onQuitar: () => void;
}> = ({ excel, cargando, deshabilitado, onArchivo, onQuitar }) => (
  <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderStyle: excel ? 'solid' : 'dashed' }}>
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <Tooltip
        arrow
        enterTouchDelay={0}
        leaveTouchDelay={8000}
        title={
          <Box sx={{ maxWidth: 300, py: 0.5 }}>
            <Typography variant="caption" fontWeight={700} display="block">
              Excel de perfiles de la OLT (opcional)
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5, lineHeight: 1.5 }}>
              Hoja <code>CLIENTES</code> con las columnas <code>SERIAL (PON)</code> y{' '}
              <code>MODELO EQUIPO (SERVICE PROFILE)</code>. Las columnas se buscan por nombre, no
              por posición. Completa el service profile de los modelos que el inventario del ERP
              no tiene y aporta line profile, plan e IDs de perfil.
            </Typography>
          </Box>
        }
      >
        <span>
          <Button
            component="label"
            variant={excel ? 'outlined' : 'contained'}
            color={excel ? 'success' : 'primary'}
            disableElevation
            startIcon={
              cargando ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />
            }
            disabled={deshabilitado || cargando}
            sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            {cargando ? 'Leyendo Excel...' : excel ? 'Reemplazar Excel' : 'Cargar Excel de perfiles'}
            <input
              hidden
              type="file"
              accept=".xlsx"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onArchivo(f);
                // Se limpia para poder volver a elegir el mismo archivo.
                e.target.value = '';
              }}
            />
          </Button>
        </span>
      </Tooltip>

      {excel ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={`${excel.filas_utiles} series`}
          />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {excel.archivo}
          </Typography>
          {/* Con `?.`: este objeto ya no viene sólo de la respuesta del
              momento, también se rehidrata desde el contexto que guarda el
              servidor. Si vuelve recortado, mejor un chip de menos que la
              pantalla entera en blanco. */}
          <Typography variant="caption" color="text.secondary">
            hoja «{excel.hoja ?? '—'}» · {excel.service_profiles?.length ?? 0} perfiles distintos
          </Typography>
          {excel.duplicados > 0 && (
            <Chip size="small" color="warning" label={`${excel.duplicados} duplicados`} />
          )}
          {excel.serial_invalido > 0 && (
            <Chip
              size="small"
              color="warning"
              label={`${excel.serial_invalido} serial(es) inválidos`}
            />
          )}
          <Button size="small" color="inherit" onClick={onQuitar} disabled={deshabilitado}>
            Quitar
          </Button>
        </Stack>
      ) : null}
    </Stack>

    {excel && excel.advertencias.length > 0 && (
      <Alert severity="warning" variant="outlined" sx={{ mt: 1.5, py: 0.5 }}>
        <Typography variant="caption" component="div" fontWeight={600}>
          {excel.advertencias.length} advertencia(s) al leer el archivo:
        </Typography>
        {excel.advertencias.slice(0, 5).map((a, i) => (
          <Typography key={i} variant="caption" component="div">
            · {a}
          </Typography>
        ))}
        {excel.advertencias.length > 5 && (
          <Typography variant="caption" component="div" sx={{ fontStyle: 'italic' }}>
            … y {excel.advertencias.length - 5} más.
          </Typography>
        )}
      </Alert>
    )}
  </Paper>
);

interface BotonPasoProps {
  numero: number;
  etiqueta: string;
  /** Qué hace el paso. Se muestra al pasar el mouse, al enfocar con teclado o al mantener pulsado en táctil. */
  ayuda: string;
  /** Motivo por el que todavía no se puede ejecutar; null cuando sí se puede. */
  bloqueo: string | null;
  cargando: boolean;
  cargandoTexto: string;
  /** Ya se ejecutó y hay resultado: el botón pasa a secundario. */
  hecho: boolean;
  /** Conteo de elementos que procesaría; se muestra junto a la etiqueta. */
  conteo?: number;
  icono: React.ReactNode;
  onClick: () => void;
}

/**
 * Botón de un paso del flujo.
 *
 * La explicación va en tooltip en vez de en un párrafo: cuatro descripciones
 * juntas eran un muro de texto que nadie leía. Pero NO queda solo en hover —
 * el Tooltip de MUI también abre con el foco de teclado y, con
 * `enterTouchDelay`, con pulsación sostenida en pantallas táctiles. Depender
 * únicamente del hover deja la información inalcanzable en táctil.
 *
 * Cuando el paso está bloqueado, el tooltip explica POR QUÉ, que es justo
 * cuando el usuario necesita la explicación.
 */
const BotonPaso: React.FC<BotonPasoProps> = ({
  numero,
  etiqueta,
  ayuda,
  bloqueo,
  cargando,
  cargandoTexto,
  hecho,
  conteo,
  icono,
  onClick,
}) => {
  const deshabilitado = cargando || bloqueo !== null;

  return (
    <Tooltip
      arrow
      // Táctil: aparece al mantener pulsado y se queda un rato para poder leerla.
      enterTouchDelay={0}
      leaveTouchDelay={8000}
      title={
        <Box sx={{ maxWidth: 300, py: 0.5 }}>
          <Typography variant="caption" fontWeight={700} display="block">
            Paso {numero} · {etiqueta}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5, lineHeight: 1.5 }}>
            {ayuda}
          </Typography>
          {bloqueo && (
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid', borderColor: 'divider' }}
            >
              <strong>Aún no disponible:</strong> {bloqueo}
            </Typography>
          )}
        </Box>
      }
    >
      {/* El span es necesario: un Button deshabilitado no emite eventos de mouse
          y el tooltip nunca abriría justo cuando más hace falta. */}
      <span>
        <Button
          variant={hecho ? 'outlined' : 'contained'}
          color={hecho ? 'success' : 'primary'}
          disableElevation
          startIcon={cargando ? <CircularProgress size={18} color="inherit" /> : icono}
          disabled={deshabilitado}
          onClick={onClick}
          sx={{
            // 44px de alto: objetivo táctil cómodo sin romper la densidad del ERP.
            minHeight: 44,
            pl: 1,
            textTransform: 'none',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          {/* El número se deja visible para lectores de pantalla: el orden del
              paso es información, no decoración. Los colores son sólidos y no
              translúcidos para no bajar del 4.5:1 de contraste. */}
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              mr: 1,
              borderRadius: '50%',
              fontSize: 12,
              fontWeight: 700,
              flexShrink: 0,
              bgcolor: deshabilitado
                ? 'action.disabledBackground'
                : hecho
                  ? 'success.main'
                  : 'common.white',
              color: deshabilitado
                ? 'text.disabled'
                : hecho
                  ? 'common.white'
                  : 'primary.main',
            }}
          >
            {numero}
          </Box>
          {cargando ? cargandoTexto : etiqueta}
          {!cargando && conteo !== undefined && conteo > 0 && (
            <Box component="span" sx={{ ml: 0.75, opacity: 0.75, fontWeight: 400 }}>
              ({conteo})
            </Box>
          )}
        </Button>
      </span>
    </Tooltip>
  );
};


const ESTADO_PREP_CFG: Record<
  IMigracionOltEstadoPrep,
  { label: string; color: 'success' | 'warning' | 'default' | 'error' }
> = {
  LISTO: { label: 'Listo', color: 'success' },
  NO_ES_HOME: { label: 'No es HOME', color: 'default' },
  YA_MIGRADO: { label: 'Ya migrado', color: 'warning' },
  FALTAN_DATOS: { label: 'Faltan datos', color: 'error' },
};


/**
 * Paso 4: previsualización del aprovisionamiento y ejecución de la migración.
 *
 * La previsualización es solo lectura. La ejecución es DESTRUCTIVA y está detrás
 * de una barrera: hay que escribir el nombre de la OLT. Entre el corte y la
 * activación el cliente queda sin servicio, así que la bitácora se pinta en vivo
 * y se puede pausar al terminar el cliente en curso.
 */
const ResultadoPaso5: React.FC<{
  data: IMigracionOltPaso5Prep;
  nombreOlt: string;
  confirmacion: string;
  onConfirmacion: (v: string) => void;
  migrando: boolean;
  pausar: boolean;
  onPausar: () => void;
  onMigrar: () => void;
  bitacora: IMigracionOltResultadoMigracion[];
  puedeMigrar: boolean;
  /** Reintento manual de la activación para quien quedó sin servicio. */
  onReintentar?: (item: IMigracionOltResultadoMigracion) => void;
  serieReintentando?: string | null;
  /** Reintento en lote de los que quedaron sin servicio en esta corrida. */
  onReintentarTodas?: () => void;
  onDetenerReintentos?: () => void;
  reintentandoTodas?: boolean;
  progresoReintentos?: { hechas: number; total: number; ok: number } | null;
  /** La corrida la maneja el servidor: se puede pausar y reanudar. */
  pausada?: boolean;
  onReanudar?: () => void;
  onCancelar?: () => void;
  /** Serie que el servidor está migrando en este momento. */
  corridaEnCursoSerie?: string | null;
}> = ({
  data,
  nombreOlt,
  confirmacion,
  onConfirmacion,
  migrando,
  pausar,
  onPausar,
  onMigrar,
  bitacora,
  puedeMigrar,
  onReintentar,
  serieReintentando,
  onReintentarTodas,
  onDetenerReintentos,
  reintentandoTodas,
  progresoReintentos,
  pausada = false,
  onReanudar,
  onCancelar,
  corridaEnCursoSerie,
}) => {
  const [filtro, setFiltro] = useState<'todos' | IMigracionOltEstadoPrep>('todos');
  const visibles = useMemo(
    () => (filtro === 'todos' ? data.resultados : data.resultados.filter((r) => r.estado === filtro)),
    [data.resultados, filtro],
  );

  /** Series cuyo JSON está desplegado. Varias a la vez para poder comparar. */
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const alternarJson = (serie: string) =>
    setAbiertos((prev) => {
      const sig = new Set(prev);
      if (sig.has(serie)) {
        sig.delete(serie);
      } else {
        sig.add(serie);
      }
      return sig;
    });

  const sinServicio = bitacora.filter((b) => b.estado_final === 'FALLO_ACTIVAR').length;
  const migradosLimpios = bitacora.filter((b) => b.estado_final === 'COMPLETADO').length;
  const acsPendiente = bitacora.filter(
    (b) => b.estado_final === 'COMPLETADO_AVISO' && b.activar_ok !== false,
  ).length;
  const aRevisar = bitacora.filter(
    (b) => b.estado_final === 'COMPLETADO_AVISO' && b.activar_ok === false,
  ).length;
  const noDesconfigurados = bitacora.filter(
    (b) => b.estado_final === 'FALLO_CANCELAR',
  ).length;
  const rechazados = bitacora.filter((b) => b.estado_final === 'RECHAZADO').length;
  // El servicio quedó arriba: eso es lo que cuenta como migrado.
  const migrados = migradosLimpios + acsPendiente;
  const todoBien = sinServicio === 0 && aRevisar === 0 && noDesconfigurados === 0;
  const confirmado = confirmacion === nombreOlt && nombreOlt.length > 0;

  return (
    <Accordion defaultExpanded variant="outlined" sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <FactCheckIcon color="primary" />
          <Typography fontWeight={600}>Paso 4 · Revisar y migrar</Typography>
          <Chip size="small" color="success" label={`${data.listos} listos`} />
          {data.ya_migrados > 0 && (
            <Chip size="small" color="warning" label={`${data.ya_migrados} ya migrados`} />
          )}
          {data.no_es_home > 0 && <Chip size="small" label={`${data.no_es_home} no HOME`} />}
          {data.faltan_datos > 0 && (
            <Chip size="small" color="error" label={`${data.faltan_datos} faltan datos`} />
          )}
          {bitacora.length > 0 && (
            <Chip
              size="small"
              color={todoBien ? 'success' : 'error'}
              icon={todoBien ? <CheckCircleIcon /> : undefined}
              label={`${migrados}/${bitacora.length} migrados`}
            />
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Metrica label="Evaluadas" value={data.evaluadas} />
          <Metrica label="Listos" value={data.listos} color="success.main" />
          <Metrica label="Ya migrados" value={data.ya_migrados} color="warning.main" />
          <Metrica label="No son HOME" value={data.no_es_home} />
          <Metrica
            label="Faltan datos"
            value={data.faltan_datos}
            color={data.faltan_datos > 0 ? 'error.main' : 'text.primary'}
          />
        </Stack>

        {/* La OLT según el catálogo del ERP: de ahí sale la VLAN del servicio. */}
        {data.olt_erp &&
          (data.olt_erp.vlan_pppoe ? (
            <Alert severity="info" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
              OLT en el catálogo del ERP: <strong>{data.olt_erp.nombre}</strong> ·{' '}
              {data.olt_erp.ip_gestion} · VLAN <strong>{data.olt_erp.vlan_pppoe}</strong>. Se usa
              esa VLAN salvo que la ONT ya tenga un service-port de cliente, en cuyo caso manda el
              de la ONT.
            </Alert>
          ) : (
            data.olt_erp.aviso && (
              <Alert severity="warning" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
                {data.olt_erp.aviso}
              </Alert>
            )
          ))}

        {/* ---------- Barrera del paso destructivo ---------- */}
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          <AlertTitle>Esto desconfigura la ONT del cliente</AlertTitle>
          Por cada cliente se ejecuta primero la <strong>desconfiguración</strong> y luego la
          reactivación. Entre una y otra el cliente <strong>queda sin internet</strong>. Si la
          activación falla, la ONT no se restaura sola: queda marcada para revisión manual.
          <br />
          La desconfiguración se reintenta hasta <strong>3 veces</strong> solo ante errores de red
          o timeout; la activación <strong>no se reintenta</strong>.
        </Alert>

        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              label="Escribe el nombre de la OLT para habilitar"
              placeholder={nombreOlt || 'OLT_...'}
              value={confirmacion}
              onChange={(e) => onConfirmacion(e.target.value.trim())}
              error={confirmacion.length > 0 && !confirmado}
              helperText={
                confirmado
                  ? 'Confirmado.'
                  : `Debe coincidir exactamente con «${nombreOlt || '(sin nombre)'}».`
              }
              disabled={migrando}
              sx={{ minWidth: 300 }}
            />
            {/* Mientras hay corrida viva no se ofrece arrancar otra: la cola
                es del servidor y una segunda corrida sobre la misma OLT
                desconfiguraría clientes desde dos procesos. */}
            {!migrando && !pausada && (
              <Button
                variant="contained"
                color="error"
                disableElevation
                startIcon={<RouterIcon />}
                disabled={!puedeMigrar || !confirmado || data.listos === 0}
                onClick={onMigrar}
                sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600, mt: 0.5 }}
              >
                {`Migrar ${data.listos} cliente(s), uno por uno`}
              </Button>
            )}

            {migrando && (
              <>
                <Button
                  variant="contained"
                  color="error"
                  disableElevation
                  startIcon={<CircularProgress size={18} color="inherit" />}
                  disabled
                  sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600, mt: 0.5 }}
                >
                  Migrando en el servidor...
                </Button>
                <Button
                  variant="outlined"
                  onClick={onPausar}
                  disabled={pausar}
                  sx={{ minHeight: 44, mt: 0.5 }}
                >
                  {pausar ? 'Pausando...' : 'Pausar'}
                </Button>
              </>
            )}

            {pausada && (
              <>
                <Button
                  variant="contained"
                  color="warning"
                  disableElevation
                  startIcon={<RouterIcon />}
                  onClick={onReanudar}
                  disabled={pausar}
                  sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600, mt: 0.5 }}
                >
                  Reanudar donde quedó
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={onCancelar}
                  disabled={pausar}
                  sx={{ minHeight: 44, mt: 0.5 }}
                >
                  Abandonar corrida
                </Button>
              </>
            )}

            {/* Reintento en lote de esta misma corrida. Aparece al terminar de
                migrar y solo si alguien quedó sin servicio: es el caso urgente
                y no hay que ir al historial a buscarlo. Repite SOLO la
                activación y acumula sobre la misma entrada. */}
            {!migrando && onReintentarTodas && sinServicio > 0 && (
              <Button
                variant="contained"
                color="warning"
                disableElevation
                startIcon={
                  reintentandoTodas ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <ReplayIcon />
                  )
                }
                disabled={reintentandoTodas}
                onClick={onReintentarTodas}
                sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600, mt: 0.5 }}
              >
                {reintentandoTodas
                  ? 'Reintentando...'
                  : `Reintentar ${sinServicio} sin servicio`}
              </Button>
            )}

            {reintentandoTodas && onDetenerReintentos && (
              <Button
                variant="outlined"
                onClick={onDetenerReintentos}
                sx={{ minHeight: 44, mt: 0.5 }}
              >
                Detener
              </Button>
            )}
          </Stack>

          {/* Avance del reintento en lote, con la misma lectura que la de la
              migración: cuántos van y cuántos recuperaron servicio. */}
          {progresoReintentos && (
            <Box sx={{ mt: 1.5 }}>
              <LinearProgress
                variant="determinate"
                value={
                  progresoReintentos.total
                    ? (progresoReintentos.hechas / progresoReintentos.total) * 100
                    : 0
                }
                color="warning"
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary">
                Reintentos: {progresoReintentos.hechas} de {progresoReintentos.total} ·{' '}
                {progresoReintentos.ok} con servicio recuperado
              </Typography>
            </Box>
          )}
        </Paper>

        {/* ---------- Bitácora ---------- */}
        {(migrando || pausada) && (
          <Alert
            severity={pausada ? 'warning' : 'info'}
            variant="outlined"
            sx={{ mb: 2 }}
          >
            <AlertTitle>
              {pausada
                ? 'Corrida pausada en el servidor'
                : 'La corrida la está ejecutando el servidor'}
            </AlertTitle>
            {pausada ? (
              <>
                Se terminó el cliente que estaba en curso y ahí paró. Reanuda cuando quieras:
                sigue desde el siguiente pendiente.
              </>
            ) : (
              <>
                Puedes recargar la página, cerrarla o entrar desde otra máquina: el proceso no
                se detiene y al volver vas a ver el avance donde esté.
                {corridaEnCursoSerie ? (
                  <>
                    {' '}Migrando ahora: <code>{corridaEnCursoSerie}</code>.
                  </>
                ) : null}
              </>
            )}
          </Alert>
        )}

        {bitacora.length > 0 && (
          <>
            {/* Resumen de la corrida: en qué quedó cada cliente. Va antes de
                la bitácora porque con 40 clientes nadie los cuenta a mano. */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 2,
                borderColor: todoBien ? 'success.main' : 'error.main',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {todoBien && !migrando && <CheckCircleIcon color="success" />}
                <Typography variant="body2" fontWeight={700}>
                  {migrando
                    ? 'Migración en curso'
                    : todoBien
                      ? 'Migración validada'
                      : 'Migración con pendientes'}
                </Typography>
                <Box sx={{ width: 8 }} />
                <Tooltip arrow title="Se desconfiguró y se volvió a aprovisionar sin novedades.">
                  <Chip size="small" color="success" label={`${migradosLimpios} migrados`} />
                </Tooltip>
                {acsPendiente > 0 && (
                  <Tooltip
                    arrow
                    title="Migrados: el servicio quedó activo. Falta que el ACS tome la configuración del ONT (hasta 20 min, se resuelve solo)."
                  >
                    <Chip
                      size="small"
                      color="success"
                      icon={<CheckCircleIcon />}
                      label={`${acsPendiente} con ACS pendiente`}
                    />
                  </Tooltip>
                )}
                {sinServicio > 0 && (
                  <Chip size="small" color="error" label={`${sinServicio} sin servicio`} />
                )}
                {aRevisar > 0 && (
                  <Tooltip arrow title="Respondió 200 pero el mensaje no confirma la activación.">
                    <Chip size="small" color="warning" label={`${aRevisar} a revisar`} />
                  </Tooltip>
                )}
                {noDesconfigurados > 0 && (
                  <Tooltip arrow title="No se pudo desconfigurar tras 3 intentos. El cliente siguió como estaba: nadie quedó caído.">
                    <Chip
                      size="small"
                      color="warning"
                      label={`${noDesconfigurados} no se desconfiguraron`}
                    />
                  </Tooltip>
                )}
                {rechazados > 0 && (
                  <Tooltip arrow title="No pasaron la validación, así que no se tocó la OLT. La consulta sí dejó guardada su configuración.">
                    <Chip size="small" variant="outlined" label={`${rechazados} rechazados`} />
                  </Tooltip>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {bitacora.length} cliente(s) procesados
                </Typography>
              </Stack>
            </Paper>

            {sinServicio > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>
                  {sinServicio} cliente(s) quedaron SIN SERVICIO
                </AlertTitle>
                Se desconfiguraron pero no se pudieron activar. Hay que entrar a la OLT a
                revisarlos. Quedan registrados en la bitácora de la base.
              </Alert>
            )}
            <Box sx={{ mb: 3 }}>
              {bitacora.map((b) => (
                <BitacoraCliente
                  key={`${b.serie}-${b.id_ejecucion ?? ""}`}
                  item={b}
                  onReintentar={onReintentar}
                  reintentando={serieReintentando === b.serie}
                />
              ))}
            </Box>
          </>
        )}

        {/* ---------- Previsualización ---------- */}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filtro}
          onChange={(_, v) => v && setFiltro(v)}
          sx={{ mb: 2, flexWrap: 'wrap' }}
        >
          <ToggleButton value="todos">Todas ({data.resultados.length})</ToggleButton>
          <ToggleButton value="LISTO">Listos ({data.listos})</ToggleButton>
          {data.ya_migrados > 0 && (
            <ToggleButton value="YA_MIGRADO">Ya migrados ({data.ya_migrados})</ToggleButton>
          )}
          {data.no_es_home > 0 && (
            <ToggleButton value="NO_ES_HOME">No HOME ({data.no_es_home})</ToggleButton>
          )}
          {data.faltan_datos > 0 && (
            <ToggleButton value="FALTAN_DATOS">Faltan datos ({data.faltan_datos})</ToggleButton>
          )}
        </ToggleButtonGroup>

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Serie</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Perfiles a enviar</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Observaciones</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  JSON
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibles.map((r) => {
                const cfg = ESTADO_PREP_CFG[r.estado];
                const d = r.payload?.datos ?? {};
                const jsonVisible = abiertos.has(r.serie);
                return (
                  <React.Fragment key={r.serie}>
                  <TableRow hover>
                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      {r.serie}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={cfg.label} color={cfg.color} variant="outlined" />
                      {r.line_profile_actual && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          en la OLT: <code>{r.line_profile_actual}</code>
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.nombre_cliente_original ?? '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        enviado como <code>{r.payload?.nombre_cliente}</code>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {d.service_profile && (
                          <Chip size="small" color="success" variant="outlined" label={d.service_profile} />
                        )}
                        {d.line_profile && (
                          <Chip size="small" color="info" variant="outlined" label={d.line_profile} />
                        )}
                        {d.traffic_table && (
                          <Chip size="small" variant="outlined" label={`TT ${d.traffic_table}`} />
                        )}
                        {d.vlan && (
                          <Tooltip arrow title={r.origen_vlan ?? 'VLAN a enviar'}>
                            <Chip size="small" variant="outlined" label={`VLAN ${d.vlan}`} />
                          </Tooltip>
                        )}
                        {d.gem_port && <Chip size="small" variant="outlined" label={`GEM ${d.gem_port}`} />}
                        {d.puerto_pon && (
                          <Chip size="small" variant="outlined" label={d.puerto_pon} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.upgrade_de_plan ? (
                        <Tooltip arrow title="Upgrade definido en la hoja 2 del Excel">
                          <Chip
                            size="small"
                            color="info"
                            label={`${r.plan_actual} → ${r.plan_migracion}`}
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {r.tipo_plan_softnet ?? '—'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {[...(r.motivos ?? []), ...(r.faltantes ?? [])].slice(0, 3).map((m, i) => (
                        <Typography key={i} variant="caption" display="block" color="text.secondary">
                          · {m}
                        </Typography>
                      ))}
                      {(r.se_resuelven_al_migrar ?? []).length > 0 && (
                        <Typography variant="caption" display="block" color="info.main">
                          Se completa al migrar: {r.se_resuelven_al_migrar?.join('; ')}
                        </Typography>
                      )}
                      {r.aviso_gem_port && (
                        <Typography variant="caption" display="block" color="warning.main">
                          · {r.aviso_gem_port}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button
                        size="small"
                        onClick={() => alternarJson(r.serie)}
                        sx={{ textTransform: 'none', minWidth: 0 }}
                      >
                        {jsonVisible ? 'Ocultar' : 'Ver JSON'}
                      </Button>
                    </TableCell>
                  </TableRow>

                  {/* Los tres cuerpos que se enviarían, en orden, para poder
                      revisarlos campo por campo antes de tocar al cliente. */}
                  {jsonVisible && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ bgcolor: 'background.default' }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Secuencia que se ejecutaría para <code>{r.serie}</code>. Todavía no se ha
                          enviado nada: esto es lo que se va a mandar.
                        </Typography>
                        <BloqueJson
                          titulo="1 · Consultar — POST /provisionamiento/cancelarSinValidacion"
                          valor={r.payload_consultar}
                        />
                        <BloqueJson
                          titulo="2 · Desconfigurar — POST /provisionamiento/cancelarSinValidacion"
                          valor={r.payload_desconfigurar}
                        />
                        <BloqueJson
                          titulo="3 · Activar — POST /provisionamiento/activarSinValidacion"
                          valor={r.payload}
                        />
                        <Typography variant="caption" color="text.secondary">
                          El <code>puerto_pon</code>, la <code>vlan</code> y el{' '}
                          <code>gem_port</code> se refrescan con la respuesta real del paso 1 justo
                          antes de enviar. La clave PPPoE viaja enmascarada aquí.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};

/** Barra de subtabs: la corrida y la traza de lo ya ejecutado. */
const SubtabsMigracion: React.FC<{
  valor: 'migracion' | 'historial';
  onCambio: (v: 'migracion' | 'historial') => void;
  sx?: object;
  /** Acciones de la pantalla, alineadas a la derecha de los subtabs. */
  acciones?: React.ReactNode;
}> = ({ valor, onCambio, sx, acciones }) => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{ borderBottom: 1, borderColor: 'divider', pr: 1, ...sx }}
  >
    <Tabs value={valor} onChange={(_, v) => onCambio(v)} sx={{ flexGrow: 1 }}>
      <Tab value="migracion" label="Migración" sx={{ textTransform: 'none', minHeight: 44 }} />
      <Tab value="historial" label="Historial" sx={{ textTransform: 'none', minHeight: 44 }} />
    </Tabs>
    {acciones}
  </Stack>
);

interface MigracionOltTabProps {
  /** El usuario puede disparar consultas (permiso migracion.ejecutar_importacion). */
  canEjecutar: boolean;
}

/**
 * Tab "Por OLT / ONT" de la pantalla de migración Softnet.
 *
 * Paso 1: el microservicio de provisionamiento lista las ONT configuradas en
 *         la OLT (SNMP walk de solo lectura) y devuelve sus seriales.
 * Paso 2: por cada serial se consulta Softnet, una petición por serie, y se
 *         muestra la respuesta cruda.
 *
 * Todo es consulta: no se modifica la OLT, ni Softnet, ni la base de datos.
 * Sirve como traza de auditoría antes de decidir qué se migra.
 */
const MigracionOltTab: React.FC<MigracionOltTabProps> = ({ canEjecutar }) => {
  const [subtab, setSubtab] = useState<'migracion' | 'historial'>('migracion');
  /** Catálogo de service profiles: la fuente extra para aprovisionar. */
  const [verServiceProfiles, setVerServiceProfiles] = useState(false);
  /**
   * Corrida (lote) de trabajo. Se genera al arrancar el paso 1 y viaja en cada
   * llamada para que el historial se pueda ver agrupado: todos los pasos que se
   * hicieron sobre la misma OLT, con el mismo archivo, en la misma sesión.
   */
  const [corridaId, setCorridaId] = useState<string>(() => crypto.randomUUID());
  const [ipOlt, setIpOlt] = useState('');
  const [paso1, setPaso1] = useState<IMigracionOltPaso1 | null>(null);
  const [paso2, setPaso2] = useState<IMigracionOltPaso2 | null>(null);
  const [paso3, setPaso3] = useState<IMigracionOltPaso3 | null>(null);
  /**
   * Progreso de los pasos por lotes. `lote`/`lotes` y `iniciado` existen para
   * que se vea que algo está pasando MIENTRAS un lote está en vuelo: con solo
   * {hechas, total} la barra se queda clavada hasta que la petición vuelve.
   */
  const [progreso, setProgreso] = useState<{
    hechas: number;
    total: number;
    lote?: number;
    lotes?: number;
    iniciado?: number;
  } | null>(null);
  /** Reloj que corre solo mientras hay progreso, para pintar el transcurrido. */
  const [ahora, setAhora] = useState(Date.now());
  useEffect(() => {
    if (!progreso) return;
    const t = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [progreso]);

  /** Excel de perfiles de la OLT (opcional): complementa la validación del paso 3. */
  const [excel, setExcel] = useState<IMigracionOltExcel | null>(null);
  /** Nombre de la OLT: obligatorio para aprovisionar y usado como confirmación. */
  const [nombreOlt, setNombreOlt] = useState('');
  /** Paso 4A: previsualización del aprovisionamiento (solo lectura). */
  const [prep, setPrep] = useState<IMigracionOltPaso5Prep | null>(null);
  /** Texto que el operador escribe para confirmar el paso destructivo. */
  const [confirmacion, setConfirmacion] = useState('');
  /** Serie cuya activación se está reintentando a mano. */
  const [serieReintentando, setSerieReintentando] = useState<string | null>(null);
  /** Reintento en lote al terminar la corrida: bandera y avance. */
  const [reintentandoTodas, setReintentandoTodas] = useState(false);
  const [progresoReintentos, setProgresoReintentos] = useState<{
    hechas: number;
    total: number;
    ok: number;
  } | null>(null);
  /** Ref y no estado: el bucle necesita el valor actual, no el capturado. */
  const detenerReintentosRef = useRef(false);

  const consultarOnt = useMigracionOltConsultarOnt();
  const iniciarConsulta = useMigracionOltConsultaIniciar();
  const accionConsulta = useMigracionOltConsultaAccion();
  const guardarContexto = useMigracionOltConsultaContexto();
  const consultarServiceProfile = useMigracionOltServiceProfile();
  const cargarExcel = useMigracionOltCargarExcel();
  const prepararAprov = useMigracionOltPrepararAprov();
  const migrarCliente = useMigracionOltMigrarCliente();
  const reintentarActivacion = useMigracionOltReintentarActivacion();

  /**
   * Entradas del paso 5: las ONT que descubrió el paso 4, enriquecidas con todo
   * lo que los pasos anteriores averiguaron. Es lo que el backend necesita para
   * armar el payload de activarSinValidacion.
   */
  const entradasPaso5 = useMemo(() => {
    if (!paso3) return [];
    const sn = new Map((paso2?.resultados ?? []).map((r) => [r.serie, r]));
    // El serial se normaliza para indexar, y se descartan filas sin serial:
    // una fila sucia del Excel no debe tumbar toda la pantalla.
    const ex = new Map(
      (excel?.perfiles ?? [])
        .filter((p) => !!p?.serial_ont)
        .map((p) => [String(p.serial_ont).toUpperCase(), p]),
    );
    return paso3.resultados
      .filter((r) => r.estado === 'CON_SERVICE_PROFILE')
      .map((r) => {
        const e = ex.get(String(r.serie ?? '').toUpperCase());
        const data0 = (sn.get(r.serie)?.response as any)?.data?.[0] ?? {};
        const dt = data0?.datos_tecnicos ?? {};
        const pi = data0?.plan_internet ?? {};
        return {
          serie: r.serie,
          // El Excel manda en el nombre; si no está, el de Softnet.
          nombre_cliente: e?.nombre ?? data0?.contrato?.nombre_cliente ?? '',
          /**
           * Lo que exige el micro de provisionamiento. Se manda la CÉDULA real
           * cuando Softnet la trae; si no, su idCliente interno, que es lo
           * único que queda y el micro solo valida que no vaya vacío.
           */
          identificacion: String(
            data0?.contrato?.identificacion || data0?.contrato?.idCliente || '',
          ),
          /**
           * La cédula, por separado y solo si es real. La usa el backend para
           * ligar al módulo de aprovisionamiento, que SÍ exige cédula/RUC
           * existente en tickets_cliente. Si viene vacía, el backend la resuelve
           * por el puente del ERP (api_contrato.numero_referencia).
           */
          cedula: String(data0?.contrato?.identificacion ?? ''),
          // El idContrato de Softnet es la llave para resolver el cliente en el
          // ERP cuando la consulta por serie no devuelve la cédula.
          id_contrato: String(data0?.contrato?.idContrato ?? ''),
          /**
           * La placa del contrato: campo "tag" de /api/obtenerContratos. El
           * backend la intercala en el nombre que manda al micro, en la segunda
           * posición (QIA-14009_AAY2358_HARO_HARO) y pone NNNN si no viene.
           */
          placa: String(data0?.contrato?.tag ?? ''),
          service_profile: r.service_profile ?? '',
          traffic_table: e?.id_line_profile ?? '',
          line_profile_antiguo: e?.line_profile ?? '',
          plan_actual: e?.plan_actual ?? '',
          plan_migracion: e?.plan_migracion ?? '',
          id_plan_migracion: e?.id_plan_migracion ?? '',
          tipo_plan: pi?.tipo_plan ?? '',
          user: dt?.usuario_pppoe ?? '',
          password: dt?.clave_pppoe ?? '',
          // puerto_pon, vlan y gem_port NO se mandan desde aquí: el backend los
          // descubre con la consulta que hace a la OLT justo antes de cortar a
          // este cliente, así que son los vigentes en ese momento.
        };
      })
      // Sin identificación el microservicio responde 400, así que se excluyen.
      .filter((i) => i.identificacion.length > 0);
  }, [paso2, paso3, excel]);

  const handlePaso5Preparar = async () => {
    if (entradasPaso5.length === 0 || !nombreOlt) return;
    setPrep(null);
    const res: any = await prepararAprov.mutateAsync({
      ip_olt: ipOlt.trim(),
      nombre_olt: nombreOlt,
      items: entradasPaso5,
      corrida_id: corridaId,
    });
    if (res?.data) {
      setPrep(res.data);
      if (consultaId) {
        guardarContexto.mutate({ id_consulta: consultaId, contexto: { prep: res.data } });
      }
    }
  };

  /**
   * Migración destructiva, cliente por cliente.
   *
   * Una petición por cliente: la OLT atiende una sesión SSH a la vez. Entre el
   * corte y la activación el cliente está sin servicio, así que el avance se
   * pinta en vivo y se puede pausar al terminar el que esté en curso.
   */
  /**
   * La migración la corre el SERVIDOR.
   *
   * Antes el bucle vivía acá y la pantalla era la dueña del proceso: recargar
   * la perdía y cerrar la pestaña dejaba al resto de los clientes sin migrar.
   * Ahora se manda la lista una vez, el micro procesa la cola cliente por
   * cliente y esto solo pregunta cómo va.
   */
  const iniciarCorrida = useMigracionOltCorridaIniciar();
  const accionCorrida = useMigracionOltCorridaAccion();

  /**
   * ¿Hay una corrida viva para esta OLT? Es lo que reconstruye la pantalla al
   * cargar. Se consulta sin IP también, para que al entrar de cero el operador
   * vea que hay algo andando aunque no haya escrito nada todavía.
   */
  /**
   * Si el servidor tiene una corrida con este id.
   *
   * Arranca en false porque corridaId nace como un uuid del navegador para
   * agrupar los pasos de consulta: preguntar por él antes de que exista daba
   * 404 en cada carga de la pantalla.
   */
  const [corridaEnServidor, setCorridaEnServidor] = useState(false);

  /**
   * El id de la FILA de corrida en el servidor, que no siempre es corridaId.
   *
   * corridaId es el id de la sesión de trabajo: agrupa los pasos en el
   * historial y nace en el navegador. Cuando la migración se arranca desde acá
   * los dos coinciden —el servidor reusa ese id—, pero al adoptar lo que ya
   * había pueden ser distintos: la consulta del paso 2 trae su sesión y la
   * corrida viene de otra. Mezclarlos pedía /corrida/{id-de-sesión}, que no
   * existe, y la pantalla mostraba un 404 que no significaba nada.
   */
  const [corridaIdServidor, setCorridaIdServidor] = useState<string | null>(null);

  /**
   * El paso 2 lo ejecuta el servidor, así que la pantalla solo lo sigue.
   *
   * Es el paso largo —una petición a Softnet por serie— y antes era un bucle
   * de acá en lotes de 20: recargar perdía todo lo consultado. Ahora se manda
   * la lista una vez y esto pregunta cómo va.
   */
  const [consultaId, setConsultaId] = useState<string | null>(null);

  /**
   * Orden desde el cual pedir resultados en el próximo sondeo.
   *
   * Ref y no estado: lo lee la función de la query, que necesita el valor del
   * momento y no el capturado en el render. Cada resultado viaja una sola vez
   * porque pesa: 72-160 kB por serie.
   */
  const desdeRef = useRef(0);
  const consultaViva = useMigracionOltConsultaActiva({ ip_olt: ipOlt.trim() });
  const consultaEstado = useMigracionOltConsultaEstado({
    id_consulta: consultaId ?? '',
    enabled: !!consultaId,
    desde: () => desdeRef.current,
  });
  const consulta = (consultaEstado.data as { data?: any } | undefined)?.data ?? null;
  const consultando = consulta?.estado === 'EN_CURSO';
  const pausadaConsulta = consulta?.estado === 'PAUSADA';

  /**
   * Si esta pantalla ya empezó su propia sesión de trabajo.
   *
   * Sin esto, la respuesta de /consulta/activa —que sigue llegando— pisaría con
   * la sesión anterior lo que el usuario acaba de arrancar con el paso 1.
   */
  const sesionPropiaRef = useRef(false);
  const adoptadoRef = useRef(false);

  /**
   * Al cargar, si el servidor tiene una consulta reciente se adopta entera:
   * pasos 1 a 3, el Excel y lo ya consultado. Es lo que hace que recargar sea
   * indistinguible de no haberse ido.
   */
  useEffect(() => {
    if (adoptadoRef.current || sesionPropiaRef.current) return;
    const viva = (consultaViva.data as { data?: any } | undefined)?.data;
    if (!viva?.hay_consulta || !viva.id_consulta) return;

    adoptadoRef.current = true;
    setConsultaId(viva.id_consulta);
    // Solo la sesión: la fila de corrida la adopta su propio efecto.
    if (viva.corrida_id) setCorridaId(viva.corrida_id);
    if (viva.ip_olt) setIpOlt(viva.ip_olt);
    const ctx = viva.contexto ?? {};
    if (viva.nombre_olt || ctx.nombre_olt) setNombreOlt(viva.nombre_olt ?? ctx.nombre_olt);
    // Se valida la forma ACÁ y no en cada render: lo que viene del contexto
    // pasó por jsonb y pudo guardarlo una versión anterior de la pantalla. Un
    // objeto recortado reventaba el render entero —"Cannot read properties of
    // undefined (reading 'length')"— y dejaba la página en blanco. Si no tiene
    // lo que el componente necesita, ese paso simplemente no se restaura.
    const paso1Ok = conArreglos<IMigracionOltPaso1>(ctx.paso1, 'series_unicas');
    const excelOk = conArreglos<IMigracionOltExcel>(
      ctx.excel,
      'service_profiles',
      'advertencias',
      'perfiles',
    );
    const paso3Ok = conArreglos<IMigracionOltPaso3>(ctx.paso3, 'resultados', 'service_profiles');
    const prepOk = conArreglos<IMigracionOltPaso5Prep>(ctx.prep, 'resultados');
    if (paso1Ok) setPaso1(paso1Ok);
    if (excelOk) setExcel(excelOk);
    if (paso3Ok) setPaso3(paso3Ok);
    if (prepOk) setPrep(prepOk);
    if (viva.paso2) {
      setPaso2(viva.paso2);
      desdeRef.current = viva.paso2.desde_siguiente ?? 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultaViva.data]);

  /**
   * Lo consultado y el avance salen del servidor mientras la consulta vive.
   *
   * Los resultados llegan por tramos y se acumulan acá. El guard del tramo
   * evita contar dos veces el mismo: solo se acumula la respuesta que
   * corresponde al puntero actual, y el servidor manda `desde: 0` cuando
   * quiere que la pantalla rearme la lista entera.
   */
  useEffect(() => {
    if (!consulta) return;
    const p2 = consulta.paso2;
    if (p2) {
      const tramo = p2.desde ?? 0;
      if (tramo === desdeRef.current) {
        setPaso2((prev) => ({
          ...p2,
          resultados:
            tramo === 0 || !prev
              ? (p2.resultados ?? [])
              : [...prev.resultados, ...(p2.resultados ?? [])],
        }));
        desdeRef.current = p2.desde_siguiente ?? 0;
      }
    }
    if (consulta.estado === 'EN_CURSO' || consulta.estado === 'PAUSADA') {
      setProgreso({
        hechas: consulta.procesados ?? 0,
        total: consulta.total ?? 0,
        iniciado: consulta.fecha_creacion ? new Date(consulta.fecha_creacion).getTime() : undefined,
      });
    } else {
      setProgreso(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consulta]);

  const corridaViva = useMigracionOltCorridaActiva({ ip_olt: ipOlt.trim() });
  const corridaEstado = useMigracionOltCorridaEstado({
    id_corrida: corridaIdServidor ?? '',
    enabled: corridaEnServidor && !!corridaIdServidor,
  });
  const corrida = (corridaEstado.data as { data?: any } | undefined)?.data ?? null;

  /**
   * La bitácora sale de la BASE, no de memoria: es lo que hace que recargar no
   * pierda nada. Mientras la corrida avanza se repregunta al mismo ritmo.
   */
  const ejecuciones = useMigracionOltEjecuciones({
    // La bitácora es de la corrida adoptada; si no hay, de esta sesión.
    corrida_id: corridaIdServidor ?? corridaId,
    enabled: corridaEnServidor && !!(corridaIdServidor ?? corridaId),
  });
  const bitacora: IMigracionOltResultadoMigracion[] =
    (ejecuciones.data as { data?: { resultados?: IMigracionOltResultadoMigracion[] } } | undefined)
      ?.data?.resultados ?? [];

  const migrando = corrida?.estado === 'EN_CURSO';
  const pausada = corrida?.estado === 'PAUSADA';
  const pausar = accionCorrida.isPending;

  /** Al entrar o al cambiar la IP, si hay corrida viva se adopta. */
  useEffect(() => {
    // Si el usuario pidió una migración nueva, lo anterior no se vuelve a
    // adoptar: el servidor sigue devolviendo la corrida reciente y sin esto
    // reaparecería sola en cuanto se refresque la consulta.
    if (sesionPropiaRef.current) return;
    const viva = (corridaViva.data as { data?: any } | undefined)?.data;
    if (viva?.hay_corrida && viva.id_corrida) {
      // Se adopta la corrida del servidor: es lo que hace que recargar la
      // página sea indistinguible de no haber salido.
      setCorridaEnServidor(true);
      setCorridaIdServidor(viva.id_corrida);
      if (viva.ip_olt && !ipOlt.trim()) setIpOlt(viva.ip_olt);
      if (viva.nombre_olt && !nombreOlt) setNombreOlt(viva.nombre_olt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corridaViva.data]);

  /** El avance lo manda el servidor mientras la corrida esté viva. */
  useEffect(() => {
    if (!corrida || (corrida.estado !== 'EN_CURSO' && corrida.estado !== 'PAUSADA')) return;
    setProgreso({ hechas: corrida.procesados ?? 0, total: corrida.total ?? 0 });
    ejecuciones.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corrida?.procesados, corrida?.estado]);

  /** Al terminar se limpia la barra y se trae la bitácora completa. */
  useEffect(() => {
    if (corrida?.estado === 'TERMINADA' || corrida?.estado === 'CANCELADA') {
      setProgreso(null);
      ejecuciones.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corrida?.estado]);

  const handleMigrar = async () => {
    const listos = (prep?.resultados ?? []).filter((r) => r.estado === 'LISTO');
    if (listos.length === 0 || confirmacion !== nombreOlt) return;

    const seriesListas = new Set(listos.map((r) => r.serie));
    const items = entradasPaso5.filter((e) => seriesListas.has(e.serie));
    if (items.length === 0) return;

    try {
      const res: any = await iniciarCorrida.mutateAsync({
        ip_olt: ipOlt.trim(),
        nombre_olt: nombreOlt,
        confirmacion,
        archivo_excel: excel?.archivo ?? null,
        // Se reusa el id con el que ya se registraron los pasos 1 a 4: si la
        // corrida naciera con otro, el historial partiría una misma sesión de
        // trabajo en dos.
        corrida_id: corridaId,
        items,
      });
      if (res?.data?.id_corrida) {
        setCorridaId(res.data.id_corrida);
        setCorridaIdServidor(res.data.id_corrida);
        setCorridaEnServidor(true);
        setProgreso({ hechas: 0, total: res.data.total ?? items.length });
      }
    } catch {
      // El hook ya reportó el detalle del backend.
    }
  };

  /** Pausar / reanudar / cancelar: es un cambio de estado en el servidor. */
  const handleAccionCorrida = async (accion: 'pausar' | 'reanudar' | 'cancelar') => {
    if (!corridaId) return;
    try {
      if (!corridaIdServidor) return;
      await accionCorrida.mutateAsync({ id_corrida: corridaIdServidor, accion });
    } finally {
      corridaEstado.refetch();
      corridaViva.refetch();
    }
  };


  /**
   * Reintenta la activación de un cliente que quedó sin servicio.
   *
   * El resultado REEMPLAZA la entrada anterior de la bitácora en vez de
   * agregarse: es el mismo cliente y el mismo corte, y el backend acumula las
   * llamadas sobre la misma fila. Dos entradas para un mismo cliente dejarían
   * la primera marcada como "sin servicio" para siempre.
   */
  const handleReintentar = async (item: IMigracionOltResultadoMigracion) => {
    if (!item.id_ejecucion) return;
    setSerieReintentando(item.serie);
    try {
      const res: any = await reintentarActivacion.mutateAsync({
        id_ejecucion: item.id_ejecucion,
        corrida_id: item.corrida_id ?? corridaId,
      });
      if (res?.data) {
        // La bitácora es del servidor: se relee en vez de parchear memoria.
        ejecuciones.refetch();
      }
    } finally {
      setSerieReintentando(null);
    }
  };

  /**
   * Reintenta la activación de TODOS los que quedaron sin servicio.
   *
   * Secuencial a propósito: la OLT atiende una sesión SSH a la vez, así que
   * lanzarlos en paralelo no acelera nada y puede dejar el equipo a medias.
   * Como el reintento individual, cada resultado reemplaza la entrada anterior
   * de la bitácora; se puede detener y termina el que esté en curso.
   */
  const handleReintentarTodas = async () => {
    const aReintentar = bitacora.filter(
      (b) => b.estado_final === 'FALLO_ACTIVAR' && !!b.id_ejecucion,
    );
    if (aReintentar.length === 0) return;

    detenerReintentosRef.current = false;
    setReintentandoTodas(true);
    setProgresoReintentos({ hechas: 0, total: aReintentar.length, ok: 0 });
    try {
      for (let i = 0; i < aReintentar.length; i += 1) {
        if (detenerReintentosRef.current) break;
        const item = aReintentar[i];
        setSerieReintentando(item.serie);
        try {
          const res: any = await reintentarActivacion.mutateAsync({
            id_ejecucion: item.id_ejecucion as string,
            corrida_id: item.corrida_id ?? corridaId,
          });
          const logrado = res?.data?.estado_final === 'COMPLETADO';
          setProgresoReintentos((pr) =>
            pr ? { ...pr, hechas: i + 1, ok: pr.ok + (logrado ? 1 : 0) } : pr,
          );
        } catch {
          // El hook ya reportó el error; se sigue con el siguiente para no
          // dejar al resto sin intentar por culpa de uno.
          setProgresoReintentos((pr) => (pr ? { ...pr, hechas: i + 1 } : pr));
        }
      }
    } finally {
      setSerieReintentando(null);
      setReintentandoTodas(false);
      ejecuciones.refetch();
    }
  };


  const ipValida = ES_IPV4.test(ipOlt.trim());
  const ocupado =
    consultarOnt.isPending ||
    iniciarConsulta.isPending ||
    consultando ||
    consultarServiceProfile.isPending ||
    cargarExcel.isPending;

  const handleExcel = async (archivo: File) => {
    const res: any = await cargarExcel.mutateAsync({ archivo, corrida_id: corridaId });
    if (res?.data) {
      setExcel(res.data);
      // El paso 3 ya corrido quedó calculado sin el archivo: se descarta para
      // que nadie lea un resultado viejo creyendo que ya incluye el Excel.
      setPaso3(null);
    }
  };

  const handleQuitarExcel = () => {
    setExcel(null);
    setPaso3(null);
  };
  const seriesDisponibles = paso1?.series_unicas ?? [];

  /**
   * Entrada del paso 3: solo las series que Softnet reconoció con contrato.
   * Las SIN_DATOS / NO_EXISTE / ERROR se quedan fuera a propósito: el cruce con
   * el inventario solo tiene sentido para clientes que existen.
   */
  const seriesConContrato = useMemo(
    () =>
      (paso2?.resultados ?? []).filter((r) => r.estado === 'ENCONTRADO').map((r) => r.serie),
    [paso2?.resultados],
  );


  const handlePaso1 = async () => {
    setPaso1(null);
    setPaso2(null);
    setPaso3(null);
    setProgreso(null);
    // Cada paso 1 arranca una corrida nueva: es el inicio de una sesion de
    // trabajo sobre una OLT, y es lo que agrupa el historial.
    const nueva = crypto.randomUUID();
    setCorridaId(nueva);
    // A partir de acá la sesión es de esta pantalla: no se adopta la del
    // servidor, que es justo la que el usuario está dejando atrás.
    sesionPropiaRef.current = true;
    setConsultaId(null);
    setCorridaIdServidor(null);
    setCorridaEnServidor(false);
    const res: any = await consultarOnt.mutateAsync({ ip_olt: ipOlt.trim(), corrida_id: nueva });
    if (res?.data) setPaso1(res.data);
  };

  /**
   * Paso 2: lo arranca en el servidor y se queda mirando.
   *
   * Antes era un for de acá en lotes de 20 series. Funcionaba, pero la
   * pantalla era la dueña del trabajo: recargar o cerrar la pestaña perdía
   * todo lo consultado, y son ~0,3-0,6 s por serie. Ahora se manda la lista
   * completa una vez; el micro recorre su cola y acá solo se pregunta cómo va.
   *
   * Se le manda además el contexto —paso 1 y el Excel ya interpretado— porque
   * es lo que hace falta para rearmar la pantalla al volver: el navegador no
   * puede releer un archivo del disco por su cuenta.
   */
  const handlePaso2 = async () => {
    if (seriesDisponibles.length === 0) return;

    setPaso2(null);
    setPaso3(null);
    desdeRef.current = 0;
    setProgreso({ hechas: 0, total: seriesDisponibles.length, iniciado: Date.now() });

    const res: any = await iniciarConsulta.mutateAsync({
      ip_olt: ipOlt.trim(),
      nombre_olt: nombreOlt || null,
      corrida_id: corridaId,
      series: seriesDisponibles,
      contexto: { paso1: paso1 ?? undefined, excel, nombre_olt: nombreOlt || undefined },
    });
    if (res?.data?.id_consulta) {
      setConsultaId(res.data.id_consulta);
      if (res.data.paso2) {
        setPaso2(res.data.paso2);
        desdeRef.current = res.data.paso2.desde_siguiente ?? 0;
      }
    } else {
      setProgreso(null);
    }
  };

  /**
   * Deja la pantalla en cero para una migración nueva.
   *
   * No borra nada del servidor: lo trabajado queda en el Historial, que lee de
   * la base. Esto solo suelta la sesión de trabajo —id nuevo, pasos vacíos— y
   * marca la sesión como propia para que lo anterior no se vuelva a adoptar.
   */
  const handleNuevaMigracion = () => {
    sesionPropiaRef.current = true;
    adoptadoRef.current = true;
    setCorridaId(crypto.randomUUID());
    setConsultaId(null);
    setCorridaIdServidor(null);
    setCorridaEnServidor(false);
    desdeRef.current = 0;
    setPaso1(null);
    setPaso2(null);
    setPaso3(null);
    setPrep(null);
    setExcel(null);
    setProgreso(null);
    setConfirmacion('');
    setIpOlt('');
    setNombreOlt('');
    setSerieReintentando(null);
    setReintentandoTodas(false);
    setProgresoReintentos(null);
  };

  /**
   * Cuándo ofrecer empezar de nuevo: hay algo en pantalla y no quedó nada
   * corriendo. Con una corrida viva el botón sería una trampa —lo que
   * corresponde ahí es Pausar o Detener—.
   */
  const hayTrabajoEnPantalla = !!paso1 || !!paso2 || !!excel || !!corrida || !!consulta;
  const puedeEmpezarDeNuevo =
    hayTrabajoEnPantalla && !migrando && !pausada && !consultando && !pausadaConsulta && !ocupado;

  /** pausar | reanudar | cancelar la consulta del paso 2. */
  const handleAccionConsulta = async (accion: 'pausar' | 'reanudar' | 'cancelar') => {
    if (!consultaId) return;
    await accionConsulta.mutateAsync({ id_consulta: consultaId, accion });
    consultaEstado.refetch();
  };

  /**
   * Paso 3: cruce contra el inventario del ERP. No va por lotes: es solo
   * lectura sobre base_erp, sin llamadas a sistemas externos, así que responde
   * en milisegundos aunque sean cientos de series.
   */
  const handlePaso3 = async () => {
    if (seriesConContrato.length === 0) return;
    setPaso3(null);
    const res: any = await consultarServiceProfile.mutateAsync({
      series: seriesConContrato,
      perfiles_excel: excel?.perfiles ?? [],
      corrida_id: corridaId,
    });
    if (res?.data) {
      setPaso3(res.data);
      // Queda guardado para que una recarga no obligue a repetir el cruce.
      if (consultaId) {
        guardarContexto.mutate({ id_consulta: consultaId, contexto: { paso3: res.data } });
      }
    }
  };


  /**
   * Los cinco pasos, en un solo lugar: etiqueta, explicación para el tooltip y
   * el motivo por el que cada uno está bloqueado.
   *
   * Tenerlos como datos y no como cuatro bloques de JSX evita que las
   * descripciones se desincronicen del estado real de los botones, que es lo
   * que pasaba cuando vivían en un párrafo aparte.
   */
  const pasos: BotonPasoProps[] = [
    {
      numero: 1,
      etiqueta: 'Consultar ONT',
      ayuda:
        'Pregunta por SNMP a la OLT qué ONT tiene configuradas y devuelve sus seriales. No modifica la OLT.',
      bloqueo: !canEjecutar
        ? 'No tienes el permiso migracion.ejecutar_olt. Pídeselo a un administrador en Gestión de accesos.'
        : !ipValida
          ? 'Escribe primero la IP de gestión de la OLT.'
          : null,
      cargando: consultarOnt.isPending,
      cargandoTexto: 'Consultando OLT...',
      hecho: !!paso1,
      icono: <SearchIcon />,
      onClick: handlePaso1,
    },
    {
      numero: 2,
      etiqueta: 'Consultar Softnet',
      ayuda:
        'Por cada serial consulta obtenerContratos de Softnet —una petición por serie— y guarda la respuesta completa, para ver qué ONT corresponden a un cliente real.',
      bloqueo: !canEjecutar
        ? 'No tienes el permiso migracion.ejecutar_olt. Pídeselo a un administrador en Gestión de accesos.'
        : seriesDisponibles.length === 0
          ? 'Requiere el paso 1: todavía no hay seriales leídos de la OLT.'
          : null,
      cargando: iniciarConsulta.isPending || consultando,
      cargandoTexto: consultando ? 'Consultando en el servidor...' : 'Consultando Softnet...',
      hecho: !!paso2,
      conteo: seriesDisponibles.length,
      icono: <CloudSyncIcon />,
      onClick: handlePaso2,
    },
    {
      numero: 3,
      etiqueta: 'Service Profile',
      ayuda: excel
        ? `De las series con contrato, resuelve el service profile cruzando DOS fuentes: el inventario del ERP y el Excel cargado (${excel.filas_utiles} series). Marca las que se contradicen.`
        : 'De las series con contrato, busca en el inventario del ERP el producto y su service profile, y separa las que lo tienen de las que no. Si cargas el Excel de la OLT, también lo usa.',
      bloqueo: !canEjecutar
        ? 'No tienes el permiso migracion.ejecutar_olt. Pídeselo a un administrador en Gestión de accesos.'
        : seriesConContrato.length === 0
          ? 'Requiere el paso 2: ninguna serie tiene contrato en Softnet todavía.'
          : null,
      cargando: consultarServiceProfile.isPending,
      cargandoTexto: 'Cruzando inventario...',
      hecho: !!paso3,
      conteo: seriesConContrato.length,
      icono: <SettingsEthernetIcon />,
      onClick: handlePaso3,
    },
    {
      // El viejo paso "Configuración ONT" se eliminó: mandaba
      // cancelar_servicios=false a TODOS los clientes en una pasada masiva.
      // Esa consulta ahora corre dentro de la migración, por cliente y justo
      // antes de su corte, así que los datos son frescos y no hay forma de
      // disparar la pasada en masa por error.
      numero: 4,
      etiqueta: 'Revisar y migrar',
      ayuda:
        'Arma el cuerpo exacto que se enviaría para reaprovisionar cada ONT y lo clasifica en Listo / No es HOME / Ya migrado / Faltan datos. Solo lectura: no toca la OLT. Es el paso previo obligatorio antes de desconfigurar a nadie. Después, al migrar, cada cliente pasa por consultar → desconfigurar → activar.',
      bloqueo: !canEjecutar
        ? 'No tienes el permiso migracion.ejecutar_olt. Pídeselo a un administrador en Gestión de accesos.'
        : !nombreOlt
          ? 'Escribe el nombre de la OLT: va en el cuerpo de la petición de aprovisionamiento.'
          : entradasPaso5.length === 0
            ? 'Requiere el paso 3: aún no hay series con service profile resuelto.'
            : null,
      cargando: prepararAprov.isPending,
      cargandoTexto: 'Validando...',
      hecho: !!prep,
      conteo: entradasPaso5.length,
      icono: <FactCheckIcon />,
      onClick: handlePaso5Preparar,
    },
  ];

  // El historial se monta aparte y lee del backend, así que no comparte estado
  // con la corrida en curso: se puede abrir en cualquier momento sin perder
  // nada de lo que ya se consultó.
  /** Botón del catálogo. Va con los subtabs para que esté en las dos vistas. */
  const botonServiceProfiles = (
    <Tooltip
      arrow
      title="Modelos de equipo que acepta el microservicio. Se usa como respaldo cuando el que resuelve el inventario o el Excel no entra, y el comodín para los que no están."
    >
      <Button
        size="small"
        startIcon={<TuneIcon />}
        onClick={() => setVerServiceProfiles(true)}
        sx={{ textTransform: 'none', minHeight: 44, whiteSpace: 'nowrap' }}
      >
        Service profiles
      </Button>
    </Tooltip>
  );

  const dialogoServiceProfiles = (
    <ServiceProfilesDialog
      abierto={verServiceProfiles}
      onCerrar={() => setVerServiceProfiles(false)}
      puedeEditar={canEjecutar}
    />
  );

  if (subtab === 'historial') {
    return (
      <Box>
        <SubtabsMigracion
          valor={subtab}
          onCambio={setSubtab}
          acciones={botonServiceProfiles}
        />
        <MigracionOltHistorial ipOltActual={ipOlt.trim()} puedeEjecutar={canEjecutar} />
        {dialogoServiceProfiles}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <SubtabsMigracion
        valor={subtab}
        onCambio={setSubtab}
        sx={{ mb: 2, mx: -3, mt: -3 }}
        acciones={botonServiceProfiles}
      />
      {dialogoServiceProfiles}

      {/* La corrida del servidor, si hay. Va arriba y por su cuenta: tras
          recargar el paso 4 no está en pantalla, y es justo ahí donde hace
          falta ver que la migración sigue andando. Los clientes se pintan acá
          solo si el paso 4 no está, para no mostrar la lista dos veces. */}
      <MigracionOltCorridaPanel
        corrida={corrida}
        bitacora={bitacora}
        puedeControlar={canEjecutar}
        onPausar={() => handleAccionCorrida('pausar')}
        onReanudar={() => handleAccionCorrida('reanudar')}
        onCancelar={() => handleAccionCorrida('cancelar')}
        ocupado={accionCorrida.isPending}
        mostrarClientes={!prep}
        onReintentar={canEjecutar ? handleReintentar : undefined}
        serieReintentando={serieReintentando}
      />

      {/* La explicación de cada paso vive en el tooltip de su botón. El aviso de
          que el paso 5 es destructivo está en su propio panel, justo antes del
          botón de ejecución: es donde el operador lo necesita, no al entrar. */}
      <CargaExcel
        excel={excel}
        cargando={cargarExcel.isPending}
        deshabilitado={!canEjecutar || ocupado}
        onArchivo={handleExcel}
        onQuitar={handleQuitarExcel}
      />

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            label="IP de gestión de la OLT"
            placeholder="10.11.106.254"
            value={ipOlt}
            onChange={(e) => setIpOlt(e.target.value)}
            error={ipOlt.length > 0 && !ipValida}
            helperText={
              ipOlt.length > 0 && !ipValida
                ? 'Debe ser una IPv4 válida (sin nombres DNS ni IPv6).'
                : 'La OLT debe estar registrada en el inventario de provisionamiento.'
            }
            disabled={ocupado}
          />
        </Grid>

        {/* Nombre de la OLT: obligatorio para el aprovisionamiento. Se escribe
            una vez y aplica a toda la corrida; además el backend lo exige como
            confirmación antes de desconfigurar a nadie. */}
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Nombre de la OLT"
            placeholder="OLT_TOLONTAG"
            value={nombreOlt}
            onChange={(e) => setNombreOlt(e.target.value.trim())}
            helperText="Se usa para todos los registros de esta corrida."
            disabled={ocupado}
          />
        </Grid>

        <Grid item xs={12}>
          {/* Los pasos son secuenciales y cada uno depende del anterior, así que
              se pintan en fila con un chevron entre ellos. En pantallas angostas
              el chevron se oculta: al envolverse apuntaría en la dirección
              equivocada y confundiría el orden. */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ pt: 0.5 }}
          >
            {pasos.map((paso, i) => (
              <React.Fragment key={paso.numero}>
                {i > 0 && (
                  <ChevronRightIcon
                    aria-hidden
                    fontSize="small"
                    sx={{ color: 'text.disabled', display: { xs: 'none', lg: 'inline-flex' } }}
                  />
                )}
                <BotonPaso {...paso} />
              </React.Fragment>
            ))}

            {/* Empezar de nuevo. Va al final de la fila de pasos, que es donde
                queda la vista después de terminar, y solo cuando no hay nada
                corriendo. Lo hecho no se pierde: queda en el Historial. */}
            {puedeEmpezarDeNuevo && (
              <>
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip
                  arrow
                  title="Limpia la pantalla para migrar otra OLT. Lo de esta sesión queda guardado en el Historial."
                >
                  <Button
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    onClick={handleNuevaMigracion}
                    sx={{ textTransform: 'none', minHeight: 44 }}
                  >
                    Realizar una nueva migración
                  </Button>
                </Tooltip>
              </>
            )}
          </Stack>

          {!canEjecutar && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.25 }}>
              No tienes el permiso migracion.ejecutar_olt. Pídeselo a un administrador en Gestión de accesos.
            </Typography>
          )}
        </Grid>
      </Grid>

      {progreso && (
        <Alert
          severity={pausadaConsulta ? 'warning' : 'info'}
          variant="outlined"
          sx={{ mt: 2 }}
          action={
            canEjecutar && consultaId && (consultando || pausadaConsulta) ? (
              <Stack direction="row" spacing={1}>
                {consultando && (
                  <>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => handleAccionConsulta('pausar')}
                      disabled={accionConsulta.isPending}
                      sx={{ textTransform: 'none', minHeight: 44 }}
                    >
                      Pausar
                    </Button>
                    {/* Detener acá no pide confirmación como en la migración:
                        esto sólo consulta a Softnet, no deja nada a medias en
                        ningún equipo. Lo peor que pasa es volver a arrancarlo. */}
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleAccionConsulta('cancelar')}
                      disabled={accionConsulta.isPending}
                      sx={{ textTransform: 'none', minHeight: 44 }}
                    >
                      Detener
                    </Button>
                  </>
                )}
                {pausadaConsulta && (
                  <>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => handleAccionConsulta('reanudar')}
                      disabled={accionConsulta.isPending}
                      sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }}
                    >
                      Reanudar donde quedó
                    </Button>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => handleAccionConsulta('cancelar')}
                      disabled={accionConsulta.isPending}
                      sx={{ textTransform: 'none', minHeight: 44 }}
                    >
                      Abandonar
                    </Button>
                  </>
                )}
              </Stack>
            ) : undefined
          }
        >
          <AlertTitle>
            {migrando
              ? `Migrando: ${progreso.hechas} de ${progreso.total} cliente(s)`
              : pausadaConsulta
                ? `Consulta pausada: ${progreso.hechas} de ${progreso.total} serie(s)`
                : `Consultando Softnet: ${progreso.hechas} de ${progreso.total} serie(s)`}
          </AlertTitle>
          {/* "buffer" y no "determinate": la parte punteada es lo que el
              servidor tiene en vuelo, así se ve que sigue trabajando aunque el
              contador no cambie entre dos preguntas. */}
          <LinearProgress
            variant="buffer"
            value={progreso.total ? (progreso.hechas / progreso.total) * 100 : 0}
            valueBuffer={
              progreso.total
                ? Math.min(
                    100,
                    ((progreso.hechas + (migrando ? 1 : (consulta?.en_proceso ?? 1))) /
                      progreso.total) *
                      100,
                  )
                : 0
            }
            sx={{ height: 8, borderRadius: 1, my: 1 }}
          />
          <Typography variant="caption" color="text.secondary" component="div">
            {progreso.iniciado && (
              <>
                Transcurrido <strong>{seg(ahora - progreso.iniciado)}</strong>.{' '}
              </>
            )}
            {!migrando && consulta?.en_curso_serie && (
              <>
                Consultando ahora:{' '}
                <strong style={{ fontFamily: 'monospace' }}>{consulta.en_curso_serie}</strong>.{' '}
              </>
            )}
            {migrando ? (
              <>
                Un cliente por vez, y cada uno hace tres llamadas a la OLT: consultar,
                desconfigurar y activar. Estimado restante:{' '}
                <strong>
                  {Math.max(1, Math.ceil(((progreso.total - progreso.hechas) * 20) / 60))} min
                </strong>
                , asumiendo ~20 s por cliente.
              </>
            ) : (
              <>
                Una petición a Softnet por serie, ~{SEGUNDOS_POR_SERIE} s cada una. Estimado
                restante:{' '}
                <strong>
                  {seg((progreso.total - progreso.hechas) * SEGUNDOS_POR_SERIE * 1000)}
                </strong>
              </>
            )}
            .
          </Typography>
          {/* Lo contrario de lo que decía antes, y es el cambio que importa:
              el trabajo vive en el micro, no en esta pestaña. */}
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
            {pausadaConsulta
              ? 'Se terminó el lote que estaba en curso y ahí paró. Al reanudar sigue desde la siguiente serie pendiente.'
              : 'Lo está ejecutando el servidor: puedes recargar, cerrar la página o entrar desde otra máquina y el proceso sigue. Al volver vas a ver el avance donde esté.'}
          </Typography>
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />

      {!paso1 && !ocupado && (
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
          Ingresa la IP de una OLT y ejecuta el paso 1 para ver los resultados.
        </Typography>
      )}

      {paso1 && <ResultadoPaso1 data={paso1} />}
      {paso2 && <ResultadoPaso2 data={paso2} />}
      {paso3 && <ResultadoPaso3 data={paso3} />}

      {prep && (
        <ResultadoPaso5
          data={prep}
          nombreOlt={nombreOlt}
          confirmacion={confirmacion}
          onConfirmacion={setConfirmacion}
          migrando={migrando}
          pausar={pausar}
          onPausar={() => handleAccionCorrida('pausar')}
          onMigrar={handleMigrar}
          pausada={pausada}
          onReanudar={() => handleAccionCorrida('reanudar')}
          onCancelar={() => handleAccionCorrida('cancelar')}
          corridaEnCursoSerie={corrida?.en_curso_serie ?? null}
          bitacora={bitacora}
          puedeMigrar={canEjecutar}
          onReintentar={canEjecutar ? handleReintentar : undefined}
          serieReintentando={serieReintentando}
          onReintentarTodas={canEjecutar ? handleReintentarTodas : undefined}
          onDetenerReintentos={() => {
            detenerReintentosRef.current = true;
          }}
          reintentandoTodas={reintentandoTodas}
          progresoReintentos={progresoReintentos}
        />
      )}
    </Box>
  );
};

export default MigracionOltTab;
